import Axios from 'services/apiClient';
import { FILTER_API_HOST } from 'Config/constants';
import queryString from 'query-string';
import {
  AIIntelligenceWithScoreItem,
  CourseDay,
  UserSummary
} from 'services/courses';
import { Filter } from 'services/filtering';
import { DateAndTime, Lang, LooseObject } from 'services/interface';
import { Review } from 'services/libraryItem/interface';

export type LIBRARY_ITEM_STATUSES_TYPE =
  | 'PUBLISHED'
  | 'PENDING'
  | 'REMOVED'
  | 'DELETED'
  | 'REJECTED';

export type ContentTypes = 'MUSIC' | 'GUIDED' | 'TALKS';
export type ContentFormat = 'VIDEO';

export interface Hashtag {
  type: string;
  name: string;
  topic: string;
  id: string;
}

export interface LibraryItemSummary {
  content_type: ContentTypes;
  content_format?: ContentFormat;
  has_background_music: boolean;
  id: string;
  is_newage: boolean;
  is_religion: boolean;
  is_science: boolean;
  is_secular: boolean;
  is_spirituality: boolean;
  item_type: 'COURSES' | 'SINGLE_TRACKS' | 'DAILY_INSIGHT';
  lang: Lang;
  media_length: number;
  media_length_group: '00-05' | '06-10' | '11-15' | '16-20' | '21-30' | '30';
  publisher: UserSummary;
  rating_score: number;
  short_description: string;
  slug: string;
  title: string;
  voice_gender: 'MALE' | 'FEMALE';
  media_paths?: string[];
  blurhash: string;
  is_premium?: boolean;
  status?: LIBRARY_ITEM_STATUSES_TYPE;
  media_paths_audio_intro?: string[];
  days?: number;
}

export interface LibraryItem {
  activity: string;
  content_type: ContentTypes;
  content_format?: ContentFormat;
  has_background_music: boolean;
  id: string;
  is_newage: boolean;
  is_religion: boolean;
  is_science: boolean;
  is_secular: boolean;
  is_spirituality: boolean;
  item_type: 'COURSES' | 'SINGLE_TRACKS' | 'DAILY_INSIGHT';
  level: string;
  lang: Lang;
  long_description: string;
  media_length: number;
  media_length_group: '00-05' | '06-10' | '11-15' | '16-20' | '21-30' | '30';
  music_type: string;
  publisher: UserSummary;
  rating_score: number;
  rating_count: number;
  play_count: number;
  short_description: string;
  slug: string;
  title: string;
  topics: string[];
  tags: string[];
  ai_tags?: string[];
  ai_tags_with_score?: { score: number; tag: string }[];
  ai_intelligence_with_score?: {
    techniques: AIIntelligenceWithScoreItem[];
  };
  voice_gender: 'MALE' | 'FEMALE';
  created_at: DateAndTime;
  _selected_reviews: Review[];
  media_paths: string[];
  media_paths_audio_intro?: string[];
  blurhash: string;
  forum_id?: string;
  duration_days?: number;
  days?: number;
  description: string;
  course_days?: CourseDay[];
  is_premium?: boolean;
  status?: LIBRARY_ITEM_STATUSES_TYPE;
  preview_media_paths?: string[];
  video_media_paths?: string[];
  media_paths_video_intro?: string[];
  approved_at?: DateAndTime;
  web_url?: string;
  standard_media_paths?: string[];
  hashtags?: Hashtag[];
  hide_transcript_from_web?: boolean;
  fallback_preview_start_at?: number;
  transcripts?: {
    transcript: string;
    english_transcript: string;
  };
}

export type LibraryItemAll = LibraryItem & LibraryItemSummary;

export const checkLibraryItemRemoved = (
  status?: LIBRARY_ITEM_STATUSES_TYPE
) => {
  return status === 'DELETED' || status === 'REMOVED' || status === 'REJECTED';
};

export const getSinglesTrack = async (slug: string) => {
  const response = await Axios.get<LibraryItemAll | null>(
    `${FILTER_API_HOST}/api/v1/libraryitems/${slug}/slug`,
    {
      timeout: 10000
    }
  );
  const { data } = response;
  const isRemovedOrRejected = checkLibraryItemRemoved(data?.status);

  return {
    singleTrackDetails: isRemovedOrRejected ? null : data,
    isRemovedOrRejected
  };
};

interface GetSinglesTrackRelatedItem {
  item_summary: {
    library_item_summary: LibraryItemAll;
  };
}

interface GetSinglesTrackRelatedParams {
  libraryItemID: string;
  filter: Filter;
}

export const getSinglesTrackRelated = async ({
  libraryItemID,
  filter
}: GetSinglesTrackRelatedParams) => {
  const query = queryString.stringify(filter);

  const response = await Axios.get<GetSinglesTrackRelatedItem[]>(
    `${process.env.REACT_APP_FILTER_API_HOST}/api/v1/libraryitems/${libraryItemID}/related?${query}`
  );
  const { data } = response;
  const totalCount = Number(response.headers['x-total-count'] ?? 0);
  const relatedLibraryItemList =
    data?.map(item => item.item_summary.library_item_summary) ?? [];

  return {
    relatedLibraryItemList,
    totalCount
  };
};

export const toLibraryItemSummary = (obj: LooseObject): LibraryItemSummary =>
  JSON.parse(JSON.stringify(obj));

export const converLibraryItemSummaryToLibraryItem = (
  libraryItemSummary?: LibraryItemSummary
): LibraryItem => {
  return {
    activity: '',
    content_type: libraryItemSummary?.content_type ?? 'MUSIC',
    has_background_music: libraryItemSummary?.has_background_music ?? false,
    id: libraryItemSummary?.id ?? '',
    is_newage: libraryItemSummary?.is_newage ?? false,
    is_religion: libraryItemSummary?.is_religion ?? false,
    is_science: libraryItemSummary?.is_science ?? false,
    is_secular: libraryItemSummary?.is_secular ?? false,
    is_spirituality: libraryItemSummary?.is_spirituality ?? false,
    item_type: libraryItemSummary?.item_type ?? 'COURSES',
    level: '',
    lang: libraryItemSummary?.lang ?? {
      iso_639_1: '',
      name: ''
    },
    long_description: libraryItemSummary?.short_description ?? '',
    media_length: libraryItemSummary?.media_length ?? 0,
    media_length_group: libraryItemSummary?.media_length_group ?? '00-05',
    music_type: '',
    publisher: libraryItemSummary?.publisher ?? {
      id: '',
      name: ''
    },
    rating_score: libraryItemSummary?.rating_score ?? 0,
    rating_count: 0,
    play_count: 0,
    short_description: libraryItemSummary?.short_description ?? '',
    slug: libraryItemSummary?.slug ?? '',
    title: libraryItemSummary?.title ?? '',
    topics: [],
    tags: [],
    voice_gender: libraryItemSummary?.voice_gender ?? 'MALE',
    created_at: {
      epoch: 0,
      iso_8601_datetime_tz: ''
    },
    _selected_reviews: [],
    media_paths: libraryItemSummary?.media_paths ?? [],
    media_paths_audio_intro: libraryItemSummary?.media_paths_audio_intro ?? [],
    blurhash: libraryItemSummary?.blurhash ?? '',
    days: libraryItemSummary?.days ?? 0,
    description: libraryItemSummary?.short_description ?? '',
    is_premium: libraryItemSummary?.is_premium ?? false,
    status: libraryItemSummary?.status ?? 'PUBLISHED'
  };
};

interface SingleTracksReviewSummary {
  id: string;
  has_publisher_reply: boolean;
  has_review_message: boolean;
  library_item_id: string;
  library_item_publisher_id: string;
  message?: string;
  owner: {
    id: string;
    name: string;
    milestone: number;
    username: string;
    has_avatar: boolean;
    region?: {
      name: string;
    };
  };
  rated_at: {
    epoch: number;
    iso_8601_datetime_tz: string;
  };
  rating: number;
}

interface SingleTracksReview {
  item_summary: {
    library_item_review_summary: SingleTracksReviewSummary;
  };
}

export const getSingleTracksReviews = async (
  libraryItemID: string,
  limit = 10
) => {
  const response = await Axios.get<SingleTracksReview[]>(
    `${process.env.REACT_APP_FILTER_API_HOST}/api/v1/single-tracks/${libraryItemID}/reviews?limit=${limit}&sort_option=rated_at&sort_direction=desc&has_review=true&rating=FIVE_STAR`
  );
  const { data } = response;
  const totalCount = Number(response.headers['x-total-count'] ?? 0);

  return {
    data,
    totalCount
  };
};

export const convertSingleTracksReviewToReview = (
  data: SingleTracksReview[]
) => {
  return data.map(item => {
    const newData: Review = {
      id: item.item_summary.library_item_review_summary.id,
      avatar: '',
      author: item.item_summary.library_item_review_summary.owner.name,
      created_at: item.item_summary.library_item_review_summary.rated_at,
      has_message:
        item.item_summary.library_item_review_summary.has_review_message,
      message: item.item_summary.library_item_review_summary.message ?? '',
      owner: {
        id: item.item_summary.library_item_review_summary.owner.id,
        name: item.item_summary.library_item_review_summary.owner.name,
        username: item.item_summary.library_item_review_summary.owner.username,
        has_avatar:
          item.item_summary.library_item_review_summary.owner.has_avatar,
        region: {
          name: item.item_summary.library_item_review_summary.owner.region?.name
        }
      },
      rating: item.item_summary.library_item_review_summary.rating,
      rated_at: item.item_summary.library_item_review_summary.rated_at,
      updated_at: item.item_summary.library_item_review_summary.rated_at
    };

    return newData;
  });
};

export const formatDurationToISO8601 = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let duration = 'PT';
  if (hours > 0) {
    duration += `${hours}H`;
  }
  if (minutes > 0) {
    duration += `${minutes}M`;
  }
  if (secs > 0 || (hours === 0 && minutes === 0)) {
    duration += `${secs}S`;
  }

  return duration;
};
